<template>
    <div>
        <breadcrumb :items="breadcrumbs" />

        <v-container fluid>
            <div class="d-flex align-center justify-space-between mt-6 mb-4">
                <h2 class="font-weight-bold mb-0">{{ programTitle }} - {{ $t('finance.loanPrograms.applicationForm') }}</h2>
                <div class="d-flex align-center">
                    <v-btn
                        v-if="isFormDirty"
                        color="primary"
                        class="mr-3"
                        :disabled="saving"
                        @click="saveApplicationForm"
                    >
                        {{ $t('Update') }}
                    </v-btn>
                    <v-btn v-if="appFormFields.length" color="primary" class="mr-3" @click="showPreviewForm = true">
                        {{ $t('finance.loanPrograms.previewForm') }}
                    </v-btn>
                    <v-btn color="primary" dark @click="$router.go(-1)">
                        {{ $t('finance.loanPrograms.backUpper') }}
                    </v-btn>
                </div>
            </div>

            <v-card flat class="pa-6">
                <StepTwo
                    ref="applicationStepTwo"
                    :data-fields="appFormFields"
                    @add-data-field="addApplicationDataField"
                    @edit-data-field="editApplicationDataField"
                    @remove-data-field="removeApplicationDataField"
                    @remove-group-field="removeApplicationGroupField"
                />
            </v-card>

            <AddDataField
                :dialog="showAddDataFieldDialog"
                :edit-field="editingField"
                @close="closeAddDataFieldDialog"
                @submit="handleApplicationFieldSubmit"
            />

            <PreviewForm v-model="showPreviewForm" :data-fields="appFormFields" />
        </v-container>
    </div>
</template>

<script>
import loadingMixin from '@/mixins/LoadingMixin';
import FinanceService from '@/_services/FinanceService';
import StepTwo from './components/StepTwo.vue';
import PreviewForm from './components/PreviewForm.vue';
import AddDataField from './components/AddDataField.vue';
import { processDataFields, transformApiFieldsToForm } from '@/utils/loanProgramFieldPayload';

export default {
    mixins: [loadingMixin],
    components: { StepTwo, PreviewForm, AddDataField },
    data() {
        return {
            programTitle: '',
            appFormFields: [],
            showPreviewForm: false,
            showAddDataFieldDialog: false,
            editingField: null,
            editingFieldIndex: null,
            formSnapshot: null,
            isFormDirty: false,
            saving: false,
            breadcrumbs: [
                {
                    text: this.$t('Finance Management'),
                    disabled: false,
                    href: '/finance-management',
                    exact: true,
                },
                {
                    text: this.$t('finance.loanPrograms.title'),
                    disabled: false,
                    href: '/finance-management/loan-programs',
                },
                {
                    text: this.$t('finance.loanPrograms.details'),
                    disabled: false,
                    href: `/finance-management/loan-programs/view/${this.$route.params.id}`,
                },
                {
                    text: this.$t('finance.loanPrograms.applicationForm'),
                    disabled: true,
                },
            ],
        };
    },
    watch: {
        appFormFields: {
            deep: true,
            handler() {
                if (this.formSnapshot == null) return;
                this.isFormDirty = this.snapshotApplicationForm(this.appFormFields) !== this.formSnapshot;
            },
        },
    },
    async mounted() {
        await this.loadData();
    },
    methods: {
        snapshotApplicationForm(fields) {
            return JSON.stringify(fields || [], (key, val) => {
                if (key === '_uid' || key === '_dateMenu') return undefined;
                return val;
            });
        },
        addApplicationDataField() {
            this.editingField = null;
            this.editingFieldIndex = null;
            this.showAddDataFieldDialog = true;
        },
        closeAddDataFieldDialog() {
            this.showAddDataFieldDialog = false;
            this.editingField = null;
            this.editingFieldIndex = null;
        },
        editApplicationDataField(index) {
            this.editingFieldIndex = index;
            this.editingField = JSON.parse(JSON.stringify(this.appFormFields[index]));
            this.showAddDataFieldDialog = true;
        },
        removeApplicationDataField(index) {
            this.appFormFields.splice(index, 1);
        },
        removeApplicationGroupField(parentIndex, childIndex) {
            const group = this.appFormFields[parentIndex];
            if (group && group.isGroup && group.groupFields && group.groupFields.length > childIndex) {
                group.groupFields.splice(childIndex, 1);
            }
        },
        handleApplicationFieldSubmit(fieldData) {
            const list = this.appFormFields;
            const isEdit = this.editingFieldIndex !== null;
            const existingField = isEdit ? list[this.editingFieldIndex] : null;

            if (fieldData.isGroup && fieldData.groupFields) {
                fieldData.groupFields.forEach((subField, subIndex) => {
                    if (isEdit && existingField && existingField.groupFields && existingField.groupFields[subIndex]) {
                        const existingSubField = existingField.groupFields[subIndex];
                        if (existingSubField.value !== undefined && existingSubField.value !== null) {
                            subField.value = existingSubField.value;
                        }
                    }
                    if (subField.value === undefined || subField.value === null) {
                        if (subField.dataType === 'check_boxes') {
                            this.$set(subField, 'value', []);
                        } else {
                            this.$set(subField, 'value', '');
                        }
                    }
                    if (subField.dataType === 'date_field') {
                        this.$set(subField, '_dateMenu', false);
                    }
                });
            } else {
                const value = isEdit && existingField && (existingField.value !== undefined && existingField.value !== null)
                    ? existingField.value
                    : this.getDefaultFieldValue(fieldData);
                if (fieldData.dataType === 'date_field') {
                    this.$set(fieldData, '_dateMenu', false);
                }
                this.$set(fieldData, 'value', value);
            }

            if (!fieldData._uid) {
                this.$set(fieldData, '_uid', `field-${Date.now()}-${Math.random().toString(36).slice(2)}`);
            }
            if (isEdit) {
                this.$set(list, this.editingFieldIndex, fieldData);
            } else {
                list.push(fieldData);
            }
            this.closeAddDataFieldDialog();
        },
        getDefaultFieldValue(field) {
            if (!field || field.isGroup) return null;
            switch (field.dataType) {
                case 'check_boxes':
                    return field.value && Array.isArray(field.value) ? field.value : [];
                default:
                    return field.value !== undefined && field.value !== null ? field.value : '';
            }
        },
        async loadData() {
            const programId = this.$route.params.id;
            if (!programId) return;

            this.formSnapshot = null;
            try {
                this.startLoading();
                const response = await FinanceService.getLoanProgramById(programId);
                const d = response?.data != null ? response.data : response;
                if (d) {
                    this.programTitle = d.programTitle || '';
                    this.appFormFields = transformApiFieldsToForm(d.applicationFormFields || [], false);
                }
            } catch (err) {
                console.error('Failed to load application form fields:', err);
                this.$notify({ title: this.$t('finance.loanPrograms.failedLoadApplicationForm'), type: 'error' });
            } finally {
                this.stopLoading();
                this.$nextTick(() => {
                    this.formSnapshot = this.snapshotApplicationForm(this.appFormFields);
                    this.isFormDirty = false;
                });
            }
        },
        async saveApplicationForm() {
            if (this.$refs.applicationStepTwo && !this.$refs.applicationStepTwo.validate()) {
                this.$notify({
                    type: 'error',
                    text: this.$t('finance.loanPrograms.fillRequiredFields'),
                });
                return;
            }

            this.saving = true;
            try {
                const fd = new FormData();
                const appResult = processDataFields(this.appFormFields, { includeValue: false });
                fd.append('applicationFields', JSON.stringify(appResult.flatFields));
                fd.append('groupedApplicationFields', JSON.stringify(appResult.groupedFields));
                await FinanceService.updateLoanProgram(this.$route.params.id, fd, {});
                this.$notify({ type: 'success', text: this.$t('finance.loanPrograms.applicationFormUpdatedSuccess') });
                await this.loadData();
            } catch (error) {
                const backendMessage =
                    error?.data?.msg ||
                    error?.data?.message ||
                    error?.response?.data?.msg ||
                    error?.response?.data?.message ||
                    null;
                this.$notify({
                    type: 'error',
                    text: backendMessage || this.$t('finance.loanPrograms.failedUpdateApplicationForm'),
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
